import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Edit, AudioFile } from '@mui/icons-material';
import { fetchAllBGM } from "../../../actions/backgroundMusicActions";
import LoadingBackdrop from "../../Common/LoadingBackdrop";

const BackgroundMusicTable = ({ searchText }) => {
  const dispatch = useDispatch();
  const background_musics = useSelector((state) => state.background_musics.background_musics);
  const loading = useSelector((state) => state.background_musics.isLoading);

  useEffect(() => {
    dispatch(fetchAllBGM());
  }, [dispatch]);

  const filteredMusics = background_musics?.filter(music =>
    music.bgm_title.toLowerCase().includes(searchText.toLowerCase())
  );

  // Helper to format seconds to mm:ss
  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '';
    const min = Math.floor(seconds / 60);
    const sec = Math.round(seconds % 60);
    return `${min}:${sec < 10 ? '0' : ''}${sec}`;
  };

  if (loading) {
    return <LoadingBackdrop />;
  }

  return (
    <Box sx={{ py: 2 }}>
      <TableContainer
        component={Paper}
        sx={{
          marginTop: 3,
          boxShadow: 2,
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell><strong>Duration</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMusics?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {searchText ? "No background music found matching your search" : "No background music available"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredMusics?.map((music) => (
                <TableRow
                  key={music.bgm_id}
                  sx={{
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    height: '48px' // More compact row height
                  }}
                >
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>{music.bgm_title}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDuration(music.bgm_audio_length)}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <Tooltip title="Open audio in new tab">
                        <IconButton
                          component="a"
                          href={music.bgm_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                          <AudioFile fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          component={Link}
                          to={`/background-musics/edit/${music.bgm_id}`}
                          size="small"
                          sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BackgroundMusicTable;
